import type { ButtonHTMLAttributes, ReactNode } from 'react'

type Variant = 'primary' | 'outline' | 'ghost' | 'danger'
type Size = 'dense' | 'large'

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  children: ReactNode
}

/**
 * Bouton pilule — 06 §6.3.
 *
 * Rayon complet, 36 px en dense et 52 px en primaire. La hauteur n'est pas
 * cosmétique : RG-11 impose 44 px de zone tactile minimale, et un bouton
 * primaire se tape au pouce sur un écran de 5 pouces.
 */
export function Button({
  variant = 'primary',
  size = 'large',
  loading = false,
  disabled,
  children,
  className = '',
  ...rest
}: Props) {
  const base =
    'inline-flex items-center justify-center rounded-full font-bold ' +
    'transition-colors duration-[120ms] disabled:opacity-50 ' +
    'disabled:cursor-not-allowed focus-visible:outline-2 ' +
    'focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]'

  const sizes: Record<Size, string> = {
    dense: 'h-9 px-4 text-[15px]',
    large: 'h-13 px-6 text-[15px] min-h-[52px]',
  }

  const variants: Record<Variant, string> = {
    primary: '',
    outline: 'border bg-transparent',
    ghost: 'bg-transparent',
    danger: '',
  }

  // RG-D1 — un bouton porte du TEXTE : 4,5:1, pas 3:1. L'accent VIF n'y
  // suffit pas (le bleu avec du blanc plafonne à 3,4:1) ; `--accent-fort` et
  // `--sur-accent` sont calculés pour tenir le seuil, accent par accent.
  const style =
    variant === 'primary'
      ? { background: 'var(--accent-fort)', color: 'var(--sur-accent)' }
      : variant === 'danger'
        ? { background: 'var(--faux-fort)', color: 'var(--sur-faux)' }
        : variant === 'outline'
          ? { borderColor: 'var(--bordure)', color: 'var(--texte-1)' }
          : { color: 'var(--accent-fort)' }

  return (
    <button
      type="button"
      {...rest}
      disabled={disabled === true || loading}
      style={style}
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
    >
      {loading ? <span aria-live="polite">…</span> : children}
    </button>
  )
}
